import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import UpcomingEvents from "@/components/UpcomingEvents";
import { Users, CalendarClock, MessageSquare, CalendarX, FileSpreadsheet, Plus, ChevronRight, Handshake } from "lucide-react";

const Tile = ({ to, icon, title, subtitle, tone = "oxford", testId }) => (
    <Link to={to} data-testid={testId}
        className="group bg-white rounded-2xl border border-slate-200 p-6 hover:border-oxford hover:shadow-lg transition-all flex flex-col">
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 ${tone === "oxford" ? "bg-oxford text-white" : tone === "sage" ? "bg-sage text-white" : tone === "terracotta" ? "bg-terracotta text-white" : "bg-seablue text-white"}`}>
            {icon}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500">{subtitle}</div>
        <div className="font-heading text-xl font-bold text-slate-900 mt-1">{title}</div>
        <div className="mt-auto pt-3 flex items-center gap-1.5 text-sm text-oxford font-semibold">
            Hap <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition" />
        </div>
    </Link>
);

export default function AdministrataDashboard() {
    const [stats, setStats] = useState({ students: 0, classes: 0, appointments: 0, collabs: 0 });
    const [upcomingAppts, setUpcomingAppts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [cls, st, ap, col] = await Promise.all([
                    api.get("/administrata/classes"),
                    api.get("/administrata/students"),
                    api.get("/administrata/director-appointments"),
                    api.get("/drejtoresha/collaborations").catch(() => ({ data: [] })),
                ]);
                const now = new Date().toISOString();
                const upcoming = (ap.data || []).filter((a) => a.scheduled_at >= now && a.status !== "cancelled").slice(0, 5);
                setUpcomingAppts(upcoming);
                setStats({
                    students: (st.data || []).length,
                    classes: (cls.data || []).length,
                    appointments: (ap.data || []).length,
                    collabs: (col.data || []).length,
                });
            } finally { setLoading(false); }
        })();
    }, []);

    return (
        <Layout>
            <div className="mb-6">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Paneli i Administratës</div>
                <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 tracking-tight">Administrata</h1>
                <p className="mt-2 text-slate-600 text-sm sm:text-base">Menaxho nxënësit, notat, mungesat, komentet dhe takimet me Drejtoreshën.</p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="administrata-stats">
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Nxënës</div>
                    <div className="text-3xl font-heading font-black text-oxford mt-1">{stats.students}</div>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Klasa</div>
                    <div className="text-3xl font-heading font-black text-oxford mt-1">{stats.classes}</div>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Takime</div>
                    <div className="text-3xl font-heading font-black text-oxford mt-1">{stats.appointments}</div>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Bashkëpunime</div>
                    <div className="text-3xl font-heading font-black text-oxford mt-1">{stats.collabs}</div>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <Tile to="/administrata/students" icon={<Users className="w-6 h-6" />} subtitle="Menaxhim" title="Nxënësit + Klasat" tone="oxford" testId="administrata-tile-students" />
                <Tile to="/administrata/appointments" icon={<CalendarClock className="w-6 h-6" />} subtitle="Takime" title="Takime me Drejtoreshën" tone="seablue" testId="administrata-tile-appts" />
                <Tile to="/drejtoresha/collaborations" icon={<Handshake className="w-6 h-6" />} subtitle="Bashkëpunime" title="Institucione & Kompani" tone="sage" testId="administrata-tile-collabs" />
                <Tile to="/teacher/comments" icon={<MessageSquare className="w-6 h-6" />} subtitle="Komentet" title="Importo & Shiko" tone="terracotta" testId="administrata-tile-comments" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="administrata-upcoming-appts">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="font-heading text-lg font-bold text-slate-900">Takimet e ardhshme</h2>
                        <Link to="/administrata/appointments" className="text-xs text-oxford font-semibold flex items-center gap-1">
                            <Plus className="w-3.5 h-3.5" /> Shto
                        </Link>
                    </div>
                    {loading ? <div className="text-sm text-slate-500">Duke ngarkuar…</div> :
                        upcomingAppts.length === 0 ? <div className="text-sm text-slate-500">Asnjë takim i ardhshëm.</div> : (
                            <div className="divide-y divide-slate-200">
                                {upcomingAppts.map((a) => (
                                    <div key={a.id} className="py-2.5 flex items-start gap-3">
                                        <div className="w-9 h-9 rounded-lg bg-seablue/10 text-seablue flex items-center justify-center shrink-0"><CalendarClock className="w-5 h-5" /></div>
                                        <div className="min-w-0 flex-1">
                                            <div className="font-bold text-slate-900 truncate">{a.requester_name}</div>
                                            <div className="text-xs text-slate-500 truncate">{a.topic}</div>
                                            <div className="text-[11px] text-oxford font-mono mt-0.5">{new Date(a.scheduled_at).toLocaleString("sq-AL")}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                </div>
                <div><UpcomingEvents days={14} limit={5} /></div>
            </div>
        </Layout>
    );
}
